import { Base } from '../../abstract/base.model';
import { StateReportOptions } from './state-report-options.model';
import { StateReportStatus } from './state-report-status.model';
import { isRecord } from '../utils/is-record';
import { toStateReportOptions } from '../utils/state-report-utils';
import { countOrZero, nullableNumber, nullableString } from '../utils/wire-value-utils';

/**
 * A state compliance report, built from the hub's state report JSON (`user-state-reports-controller.ts`,
 * hub `data-models/state-report.ts`). `downloadUrl` is a pre-signed URL, only sent when fetching one report.
 *
 * The hub has no update route, so every field is read-only; fields the hub didn't send stay `undefined`
 * and an explicit `null` stays `null`. Display fallbacks belong in templates.
 */
export class StateReport extends Base {
  organizationId?: string | null;
  name?: string | null;
  /** 2-letter state code. */
  state?: string | null;
  /** 'all', 'pesticide', 'fertilizer', 'seeding' or 'other'. */
  reportType?: string | null;
  /** Missing status reads as 'draft', the hub's initial state. */
  status: StateReportStatus;
  startDate?: string | null;
  endDate?: string | null;
  options?: StateReportOptions | null;
  reportUrl?: string | null;
  downloadUrl?: string | null;
  generationProgress: number;
  recordCount?: number | null;
  fileSize?: string | null;
  submittedAt?: string | null;
  submittedTo?: string | null;
  errorMessage?: string | null;
  createdBy?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.organizationId = nullableString(source['organizationId']);
    this.name = nullableString(source['name']);
    this.state = nullableString(source['state']);
    this.reportType = nullableString(source['reportType']);
    this.status = (nullableString(source['status']) ?? 'draft') as StateReportStatus;
    this.startDate = nullableString(source['startDate']);
    this.endDate = nullableString(source['endDate']);
    this.options = toStateReportOptions(source['options']);
    this.reportUrl = nullableString(source['reportUrl']);
    this.downloadUrl = nullableString(source['downloadUrl']);
    this.generationProgress = countOrZero(source['generationProgress']);
    this.recordCount = nullableNumber(source['recordCount']);
    this.fileSize = nullableString(source['fileSize']);
    this.submittedAt = nullableString(source['submittedAt']);
    this.submittedTo = nullableString(source['submittedTo']);
    this.errorMessage = nullableString(source['errorMessage']);
    this.createdBy = nullableString(source['createdBy']);
  }
}
